import { FormControl, Paper, Stack } from "@mui/material";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect } from "react";
import {
  CustomTextField,
  CustomToggleCompoment,
  CustomMultiTagComponent,
} from "../../../ui/input";
import { CustomTypographyComponent } from "../../../ui/text";
import debounce from "lodash.debounce";
import { ObjectFormSchema, ObjectUiSchema } from "../types";

interface StateValue {
  formSchema: ObjectFormSchema;
  uiSchema: ObjectUiSchema;
  name: string;
}

interface FormObjectViewerProps {
  inputPropertyData: any;
  onFormStateChange: (formState) => void;
}

const FormObjectViewer = ({
  inputPropertyData,
  onFormStateChange,
}: FormObjectViewerProps) => {
  const [formPropertiesState, setFormPropertiesState] =
    React.useState<StateValue>(null);

  const [requiredArrayItems, setRequiredArrayItems] = React.useState([]);
  const t = useTranslations();

  const getRequiredArray = (inputProps: any) => {
    const requiredArr = Object.keys(inputProps.properties).map((key) => ({
      id: key,
      name: inputProps.properties[key].title,
    }));
    return requiredArr || [];
  };

  useEffect(() => {
    if (inputPropertyData && inputPropertyData.properties) {
      setRequiredArrayItems(getRequiredArray(inputPropertyData));
    }

    setFormPropertiesState({
      ...inputPropertyData,
      required: inputPropertyData.required || [],
    });
  }, [inputPropertyData]);

  const debouncedSave = useCallback(
    debounce((nextValue) => onFormStateChange(nextValue), 1000),
    [onFormStateChange] // will be created only once initially
  );

  const handleChangeFormSchema =
    (prop: keyof StateValue["formSchema"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (prop === "part") {
        setFormPropertiesState((prevState: StateValue) => {
          debouncedSave({
            ...prevState,
            formSchema: {
              ...prevState.formSchema,
              [prop]: event.target.checked,
            },
          });
          return {
            ...prevState,
            formSchema: {
              ...prevState.formSchema,
              [prop]: event.target.checked,
            },
          };
        });
      } else {
        setFormPropertiesState((prevState: StateValue) => {
          debouncedSave({
            ...prevState,
            formSchema: { ...prevState.formSchema, [prop]: event.target.value },
          });
          return {
            ...prevState,
            formSchema: { ...prevState.formSchema, [prop]: event.target.value },
          };
        });
      }
    };

  const handleChangeUISchema =
    (prop: keyof StateValue["uiSchema"]["ui:options"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const prevUiOptions =
        (formPropertiesState.uiSchema &&
          formPropertiesState.uiSchema["ui:options"]) ||
        null;
      setFormPropertiesState((prevState: StateValue) => {
        debouncedSave({
          ...prevState,
          uiSchema: {
            ...prevState.uiSchema,
            "ui:options": {
              ...prevUiOptions,
              [prop]: event.target.checked,
            },
          },
        });
        return {
          ...prevState,
          uiSchema: {
            ...prevState.uiSchema,
            "ui:options": {
              ...prevUiOptions,
              [prop]: event.target.checked,
            },
          },
        };
      });
    };

  const handleRequiredFieldAdded = (items: any[]) => {
    const objIDs = items.map((item) => item.id);
    //@ts-ignore
    setFormPropertiesState((prevState: StateValue) => {
      debouncedSave({
        ...prevState,
        formSchema: { ...prevState.formSchema, required: objIDs },
      });
      return {
        ...prevState,
        formSchema: { ...prevState.formSchema, required: objIDs },
        uiOptions: {
          ...prevState.uiSchema,
        },
      };
    });
  };

  const handleRenderFormSchema = (item) => (
    <Stack padding={3} spacing={2} direction="column" height={250} width={320}>
      <FormControl sx={{ width: 300 }}>
        <CustomTextField
          controldisabled={false}
          id="outlined-adornment-title"
          value={
            (formPropertiesState &&
              formPropertiesState.formSchema &&
              formPropertiesState.formSchema.title) ||
            ""
          }
          label={t("labels.title")}
          onChange={handleChangeFormSchema("title")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomTextField
          controldisabled={false}
          id="outlined-adornment-description"
          value={
            (formPropertiesState &&
              formPropertiesState.formSchema &&
              formPropertiesState.formSchema.description &&
              formPropertiesState.formSchema.description) ||
            ""
          }
          label={t("labels.description")}
          onChange={handleChangeFormSchema("description")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeFormSchema("part")}
          checked={
            (formPropertiesState &&
              formPropertiesState.formSchema &&
              formPropertiesState.formSchema.part &&
              formPropertiesState.formSchema.part) ||
            false
          }
          label={t("labels.part")}
          name={t("labels.part")}
        />
      </FormControl>

      <FormControl sx={{ width: 300 }}>
        <CustomMultiTagComponent
          // controldisabled={false}
          maxHeight={200}
          tagOptions={requiredArrayItems}
          label={t("labels.requiredField")}
          disableCloseOnSelect={true}
          inputValue={
            formPropertiesState.formSchema &&
            formPropertiesState.formSchema.required
          }
          onTagValueChange={handleRequiredFieldAdded}
        />
      </FormControl>
    </Stack>
  );

  const renderUIOptions = (item) => (
    <Stack padding={3} spacing={2} direction="column" height={280} width={500}>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("fullWidth")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].fullWidth) ||
            false
          }
          label={t("labels.fullWidth")}
          name={t("labels.fullWidth")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("newLine")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].newLine) ||
            false
          }
          label={t("labels.newLine")}
          name={t("labels.newLine")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("autofocus")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].autofocus) ||
            false
          }
          label={t("labels.autofocus")}
          name={t("labels.autofocus")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("hideInApp")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].hideInApp) ||
            false
          }
          label={t("labels.hideInApp")}
          name={t("labels.hideInApp")}
        />
      </FormControl>
      {formPropertiesState.name === "root" && (
        <FormControl sx={{ width: 300 }}>
          <CustomToggleCompoment
            controldisabled={false}
            onChange={handleChangeUISchema("hideMenu")}
            checked={
              (formPropertiesState.uiSchema &&
                formPropertiesState.uiSchema["ui:options"] &&
                formPropertiesState.uiSchema["ui:options"].hideMenu) ||
              false
            }
            label={t("labels.hideMenu")}
            name={t("labels.hideMenu")}
          />
        </FormControl>
      )}
    </Stack>
  );
  return (
    <Stack id="FormBuilderPropertiesView">
      <Paper>
        <Stack spacing={2}>
          <Stack mb={3}>
            <Stack alignItems={"center"}>
              <CustomTypographyComponent variant="h6">
                {t("labels.formSchema")}
              </CustomTypographyComponent>
              <CustomTypographyComponent variant="h6">
                {formPropertiesState && formPropertiesState.name}
              </CustomTypographyComponent>
            </Stack>
            {formPropertiesState && handleRenderFormSchema(formPropertiesState)}
          </Stack>
          <Stack>
            <Stack mt={10} alignItems={"center"}>
              <CustomTypographyComponent variant="h6">
                {t("labels.uiSchema")}
              </CustomTypographyComponent>
            </Stack>
            <Stack alignItems={"center"}>
              <CustomTypographyComponent variant="body2">
                {t("lables.uiOptions")}
              </CustomTypographyComponent>
            </Stack>

            {formPropertiesState && renderUIOptions(formPropertiesState)}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default FormObjectViewer;
